
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ProfileInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import {
  EmployeeLeavesInterface,
  LeaveRequestInterface,
  LeavesRepositoryInterface,
} from '@/services/Calendar/leaves/interfaces';
import { DataTableHeader } from 'vuetify';

interface EmployeeLeavesTableInterface {
  userId: string,
  name: string,
  remainingDays: string,
  lastLeaveTypeUsed: string,
  dates: string,
  upcomingRequest: string
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'leavesRepository',
  ],
})
export default class EmployeesLeaves extends Vue {
  private profileRepository!: ProfileRepositoryInterface;
  private leavesRepository!: LeavesRepositoryInterface;

  private loading: boolean = true;

  private itemsPerPage: number = 10;
  private currentPage: number = 1;
  private totalItems: number = 0;

  private profiles: ProfileInterface[] = [];
  private allLeaves: LeaveRequestInterface[] = [];
  private employeeLeaves: EmployeeLeavesTableInterface[] = [];
  private employeesLeaves: EmployeeLeavesInterface[] = [];

  private headers: DataTableHeader[] = [
    { text: 'Name', value: 'name' },
    { text: 'Remaining Days', value: 'remainingDays' },
    { text: 'Last Leave Type Used', value: 'lastLeaveTypeUsed' },
    { text: 'Dates', value: 'dates' },
    { text: 'Upcoming Leave', value: 'upcomingRequest' },
  ];

  public async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.loading = true;
    await this.getProfiles();
    await this.getProfilesLeaves();
    await this.getEmployeeLeaves();
    await this.aggregateProfilesLeaves();
    this.loading = false;
  }

  private aggregateProfilesLeaves(): void {
    this.employeeLeaves = [];
    this.profiles.forEach((profile) => {
      const lastLeave = this.allLeaves.filter(
        (leave) => leave.requestBy === profile.userId && new Date(leave.dateTo) < new Date(),
      ).sort((leaveA, leaveB) => new Date(leaveB.dateTo).getTime() - new Date(leaveA.dateTo).getTime())
        .shift();

      const upcomingLeave = this.allLeaves.filter(
        (leave) => leave.requestBy === profile.userId && new Date(leave.dateFrom) > new Date(),
      ).sort((leaveA, leaveB) => new Date(leaveA.dateFrom).getTime() - new Date(leaveB.dateFrom).getTime())
        .shift();

      const employeeIndex = this.employeesLeaves.findIndex((employee) => employee.userId === profile.userId);

      const data: EmployeeLeavesTableInterface = {
        userId: profile.userId,
        name: `${profile.personalDetails.firstName} ${profile.personalDetails.lastName}`,
        remainingDays: employeeIndex !== -1
          ? String(this.employeesLeaves[employeeIndex].paidLeaves)
          : 'No Paid Leaves Data',
        lastLeaveTypeUsed: lastLeave ? lastLeave.leaveType.name : 'Haven\'t used.\t',
        dates: lastLeave ? `${Vue.prototype.convertTimestamp(lastLeave.dateFrom, true)}
        - ${Vue.prototype.convertTimestamp(lastLeave.dateTo, true)}` : 'Haven\'t used.\t',
        upcomingRequest: upcomingLeave
          ? `${upcomingLeave.leaveType.name}: ${Vue.prototype.convertTimestamp(upcomingLeave.dateFrom, true)}
          - ${Vue.prototype.convertTimestamp(upcomingLeave.dateTo, true)}`
          : 'Haven\'t used.\t',
      };

      this.employeeLeaves.push(data);
    });
  }

  private async getProfilesLeaves(): Promise<void> {
    this.allLeaves = await this.leavesRepository.getByUsersIds(this.profiles.map((profile) => profile.userId));
  }

  private async getEmployeeLeaves(): Promise<void> {
    this.employeesLeaves = await this.leavesRepository
      .getEmployeeLeavesByIds(this.profiles.map((profile) => profile.userId));
  }

  private async getProfiles(): Promise<void> {
    this.profiles = [];
    [this.profiles, this.totalItems] = await this.profileRepository.getAllProfiles(this.currentPage);
  }

  private async changePage(): Promise<void> {
    await this.initialize();
    this.$forceUpdate();
  }
}
